import { Component, OnInit, OnDestroy, Input, signal } from '@angular/core';
import { catchError, forkJoin, map, Observable } from 'rxjs';
import { StressResultsService } from '../../services/stress-results.service';
import { PeriforOnChangeMessages, SignalRService } from '../../../shared/services/signal-r.service';
import { WellConfigService } from 'src/app/shared/services/well-config.service';
import { DesignFactorsService } from '../../services/design-factors.service';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { SessionStorageKeys, StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { DesignLimitsPlotUi } from '../../models/perivis-results-state.model';
import { Store } from '@ngneat/elf';
import { withUIEntities } from '@ngneat/elf-entities';
import { XyLinePlotUi } from 'src/app/core/models/xy-line-plot.model';
import { DesignLimitsPlotDto, DesignLimitsResultsModel } from '../../models/design-limits-results.model';
import { CurrentAppIds } from 'src/app/core/components/user-admin-page/user-model';
import { formatUnitNumber } from 'src/app/shared/helpers/unit-number-format.helper';

@Component({
  selector: 'app-design-limits-plot',
  templateUrl: './design-limits-plot.component.html',
  styles: [],
  standalone: false
})
export class DesignLimitsPlotComponent implements OnInit, OnDestroy {

  private _plotData: { label: string; value: any[] };

  private connectionTleData: any[] = [];
  private connectionCqeData: any[] = [];
  private connectionBurstLimit: number[] = [];
  private connectionTensionLimit: number[] = [];
  private connectionCompressionLimit: number[] = [];
  private connectionCollapseLimit: number[] = [];
  private _userUnits: UserUnitsModel;
  private _currentString: any;
  private selectedString: string;

  public dlpData: DesignLimitsResultsModel;
  public isLoading = signal<boolean>(true);
  public configMissing: boolean;
  public needToCalculate: boolean;
  public stringSections: { label: string; displayLabel: string; value: any[] }[] = [];
  public readonly formatStringSectionOption = (option: { displayLabel: string }): string => option.displayLabel;

  public xAxisTitle = '';
  public yAxisTitle = '';
  public plotName: string;
  public downloadPlotName = 'design_limits_plot';

  public plot = {
    data: [],
    layout: {},
    config: {}
  };

  //State Management
  private _componentId: string;
  @Input() set componentId(value: string) {
    this._componentId = value;
    this.designLimitsPlotStore = this._storeService.createStore(this.componentId, new DesignLimitsPlotUi, withUIEntities<XyLinePlotUi>());
    this.selectedStringSection = this.designLimitsPlotStore.state.selectedStringSection || null;
  }
  get componentId(): string {
    return this._componentId;
  }
  public designLimitsPlotStore: Store;

  public selectedStringSection: any = null;

  constructor(
    private _stressResultsService: StressResultsService,
    private _signalRService: SignalRService,
    private _wellConfigService: WellConfigService,
    private _designFactorsService: DesignFactorsService,
    private _storeService: StoreService
  ) { }

  async ngOnInit(): Promise<void> {
    this._userUnits = await this._storeService.get<UserUnitsModel>(StorageKeys.UNITS);

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));

    const appIds = this._storeService.getSession<CurrentAppIds>(SessionStorageKeys.APP_IDS);
    this.selectedString = appIds.tubularId;
    this.getDesignLimits();
  }

  signalRfunc(data: any) {
    if (data.action == PeriforOnChangeMessages.REFRESH_RESULTS || data.action == PeriforOnChangeMessages.REFRESH_DESIGN_LIMITS_PLOT || data.action == PeriforOnChangeMessages.REFRESH_STRING_INPUTS) {
      const appIds = this._storeService.getSession<CurrentAppIds>(SessionStorageKeys.APP_IDS);
      this.selectedString = appIds.tubularId;
      this.getDesignLimits();
      this.designLimitsPlotStore.reset();
    }
  }

  setPlotName(): string {
    return 'dlpPlot - ' + this.selectedString + ' - ' + this.selectedStringSection;
  }

  getDesignLimits() {
    this.isLoading.set(true);
    this._wellConfigService.getTubular().subscribe({
      next: (currentString) => {
        if (currentString != null) {
          this._currentString = currentString;
          const sources: Observable<any>[] = [
            this._stressResultsService.getDesignLimitsPlots(),
            this._designFactorsService.getDesignFactors() as Observable<any>
          ];

          forkJoin(sources).pipe(
            map(([dlp, designFactors]) => {
              this.connectionTensionLimit = [];
              this.connectionCompressionLimit = [];
              this.connectionBurstLimit = [];
              this.connectionCollapseLimit = [];
              this.connectionTleData = [];
              this.connectionCqeData = [];
              this.dlpData = dlp;

              this.stringSections = dlp?.plots?.map((ss: DesignLimitsPlotDto) => {
                const index = Number(ss.section.slice(ss.section.length - 2)) - 1;
                const sectionTop = currentString.stringSections[index - 1]?.bottomMeasuredDepth.toLocaleString('en-US') ?? currentString.hangerMd.toLocaleString('en-US');
                const stringSection: any = currentString.stringSections[index];
                const sectionBottom = stringSection.bottomMeasuredDepth.toLocaleString('en-US');
                const diameterLabel = this._userUnits.shortLengths == 'in' ? '"' : this._userUnits.shortLengths;
                const weigthUnit = this._userUnits.linearDensity == 'lb/ft' ? '#' : this._userUnits.linearDensity;
                const sectionLabel = 'Section ' + (index + 1) + ': ' + stringSection.pipe.outsideDiameter + `${diameterLabel}, ` + stringSection.pipe.weightPerFoot + `${weigthUnit}, ` + stringSection.grade.name + '  ' + '[' + sectionTop + ' - ' + sectionBottom + ` ${this._userUnits.longLengths}MD]`;
                const diameterUnit = this._userUnits.shortLengths;
                const diameter = formatUnitNumber(stringSection.pipe.outsideDiameter, diameterUnit, undefined, undefined, {
                  [diameterUnit]: diameterUnit === 'in' ? 3 : 2
                });
                const weight = formatUnitNumber(stringSection.pipe.weightPerFoot, this._userUnits.linearDensity);
                const displayLabel = `Section ${index + 1}: ${diameter}${diameterLabel}, ${weight}${weigthUnit}, ${stringSection.grade.name}  [${sectionTop} - ${sectionBottom} ${this._userUnits.longLengths}MD]`;

                if (stringSection?.connection) {
                  const tensionRating = stringSection?.connection.tensionRating;
                  const compressionRating = stringSection?.connection.compressionRating;
                  const burstRating = stringSection?.connection.burstRating;
                  const collapseRating = stringSection?.connection.collapseRating;

                  this.connectionTensionLimit.push(+(tensionRating / (designFactors.connection?.tension ?? 1.3)).toFixed(2));
                  this.connectionCompressionLimit.push(+(-compressionRating / (designFactors.connection?.compression ?? 1.15)).toFixed(2));
                  this.connectionBurstLimit.push(+(burstRating / (designFactors.connection?.burstLeak ?? 1.1)).toFixed(2));
                  this.connectionCollapseLimit.push(+(-collapseRating / (designFactors.connection?.collapse ?? 1)).toFixed(2));

                  this.connectionTleData.push(stringSection.connection.tleProfile);
                  this.connectionCqeData.push(this.getConnectionCqeData(stringSection.connection.tleProfile, designFactors));
                } else {
                  this.connectionTensionLimit.push(null);
                  this.connectionCompressionLimit.push(null);
                  this.connectionBurstLimit.push(null);
                  this.connectionCollapseLimit.push(null);
                  this.connectionTleData.push(null);
                  this.connectionCqeData.push(null);
                }

                return { label: sectionLabel, displayLabel, value: ss.designLimitsPlot }
              }) ?? [];

              this.xAxisTitle = 'Axial Force (' + this._userUnits.force + ')';
              this.yAxisTitle = 'Effective Pressure (' + this._userUnits.pressure + ')';

              if (this.stringSections.length === 0) {
                this.needToCalculate = true;
                this.isLoading.set(false);
                this.plotDesignLimits([]);
              } else {
                this.needToCalculate = false;
                const stateStringSectionExists = this.stringSections.find(stringSection => stringSection.label == this.selectedStringSection);
                if (stateStringSectionExists) {
                  this._plotData = stateStringSectionExists
                } else {
                  this._plotData = this.stringSections[0];
                  this.selectedStringSection = this.stringSections[0].label;
                  this.designLimitsPlotStore.update(state => ({ ...state, selectedStringSection: this.stringSections[0].label }));
                }

                if (!this.selectedStringSection) {
                  this.selectedStringSection = this.stringSections[0].label;
                  this.designLimitsPlotStore.update(state => ({ ...state, selectedStringSection: this.stringSections[0].label }))
                };
                this.plotName = this.setPlotName();
                // this._plotData = this.stringSections[0];
                this.plotDesignLimits(this._plotData.value);
              }
            }),
            catchError(err => {
              this.isLoading.set(false);
              this.configMissing = true;
              console.log(err);
              return err;
            })).subscribe();
        } else {
          this.configMissing = true;
          this.isLoading.set(false);
        }
      }
    });
  }

  private getConnectionCqeData(tleProfile: any[], designFactors: any): any[] {
    if (!tleProfile || tleProfile.length === 0) {
      return [];
    }

    const burst = designFactors.connection.envelopeBurst ?? 1;
    const collapse = designFactors.connection.envelopeCollapse ?? 1;

    const scale = (row: any) => {
      const factor = row.pressure >= 0 ? burst : collapse;
      return {
        axialLoad: +(row.axialLoad / factor).toFixed(2),
        pressure: +(row.pressure / factor).toFixed(2)
      };
    };

    const cqeData: any[] = [];
    for (let i = 0; i < tleProfile.length; i++) {
      const curr = tleProfile[i];
      const next = tleProfile[(i + 1) % tleProfile.length];
      cqeData.push(scale(curr));

      const crossesZero = (curr.pressure > 0 && next.pressure < 0) || (curr.pressure < 0 && next.pressure > 0) || (curr.pressure === 0 && next.pressure !== 0) || (curr.pressure !== 0 && next.pressure === 0);
      if (crossesZero) {
        const t = curr.pressure / (curr.pressure - next.pressure);
        const xAtZero = curr.axialLoad + t * (next.axialLoad - curr.axialLoad);
        const fromFactor = curr.pressure >= 0 ? burst : collapse;
        const toFactor = next.pressure >= 0 ? burst : collapse;
        cqeData.push({ axialLoad: +(xAtZero / fromFactor).toFixed(2), pressure: 0 });
        cqeData.push({ axialLoad: +(xAtZero / toFactor).toFixed(2), pressure: 0 });
      }
    }

    return cqeData;
  }

  selectedStringSectionUpdated(e, originalEvent) {
    this.selectedStringSection = e.value;
    this.designLimitsPlotStore.update(state => ({ ...state, selectedStringSection: e.value }));
    this.plotName = this.setPlotName();
    this.plotDesignLimits(this.stringSections.find(stringSection => stringSection.label == this.selectedStringSection).value, originalEvent);
  }

  plotDesignLimits(plotData, originalEvent?) {
    this._plotData = [] as any;
    this.plot.data = [];
    if (plotData.length === 0) {
      return;
    }

    plotData = plotData.filter((thing, index, self) =>
      index != self.findIndex((t) => (
        t.label != thing.label
      ))
    );

    plotData.forEach(element => {
      element.x.forEach((x, i) => {
        element.x[i] = (Math.trunc(x * 100) / 100);
      });

      element.y.forEach((y, i) => {
        element.y[i] = (Math.trunc(y * 100) / 100);
      });
    });

    if (this.connectionBurstLimit.length > 0) {
      let index = originalEvent ? this.stringSections.findIndex(x => x.label == originalEvent?.currentTarget.ariaLabel) : 0;
      if (this.selectedStringSection) {
        index = this.stringSections.findIndex(x => x.label == this.selectedStringSection);
      }

      const burstLine = {
        name: "Connection Burst",
        x: [this.connectionCompressionLimit[index], this.connectionTensionLimit[index]],
        y: [this.connectionBurstLimit[index], this.connectionBurstLimit[index]],
        line: {
          color: '#3366CC',
          dash: 'dot'
        },
        width: 11
      }

      const tensionLine = {
        name: "Connection Tension",
        x: [this.connectionTensionLimit[index], this.connectionTensionLimit[index]],
        y: [this.connectionBurstLimit[index], this.connectionCollapseLimit[index]],
        line: {
          color: '#FF9900',
          dash: 'dot'
        },
        width: 11
      }

      const collapseLine = {
        name: "Connection Collapse",
        x: [this.connectionCompressionLimit[index], this.connectionTensionLimit[index]],
        y: [this.connectionCollapseLimit[index], this.connectionCollapseLimit[index]],
        line: {
          color: '#AB63FA',
          dash: 'dot'
        },
        width: 11
      }

      const compressionLine = {
        name: "Connection Compression",
        x: [this.connectionCompressionLimit[index], this.connectionCompressionLimit[index]],
        y: [this.connectionBurstLimit[index], this.connectionCollapseLimit[index]],
        line: {
          color: 'red',
          dash: 'dot'
        },
        width: 11
      }

      if (burstLine.x[0] != null) {
        plotData.splice(6, 0, burstLine);
        plotData.splice(7, 0, tensionLine);
        plotData.splice(8, 0, collapseLine);
        plotData.splice(9, 0, compressionLine);
      }

      if (this.connectionTleData[index]?.length > 0) {
        const xArr = this.connectionTleData[index].map(p => p.axialLoad);
        const yArr = this.connectionTleData[index].map(p => p.pressure);

        if (xArr[0] !== xArr[xArr.length - 1] || yArr[0] !== yArr[yArr.length - 1]) {
          xArr.push(xArr[0]);
          yArr.push(yArr[0]);
        }

        const tleLine = {
          name: "Connection CQE",
          x: xArr,
          y: yArr,
          line: { color: 'green', dash: 'dot' },
          width: 11
        };

        plotData.splice(10, 0, tleLine);

        const cseXArr = this.connectionCqeData[index].map(p => p.axialLoad);
        const cseYArr = this.connectionCqeData[index].map(p => p.pressure);

        if (cseXArr[0] !== cseXArr[cseXArr.length - 1] || cseYArr[0] !== cseYArr[cseYArr.length - 1]) {
          cseXArr.push(cseXArr[0]);
          cseYArr.push(cseYArr[0]);
        }

        const cqeLine = {
          name: "Connection CDE",
          x: cseXArr,
          y: cseYArr,
          line: { color: 'brown', dash: 'dot' },
          width: 11
        };

        plotData.splice(11, 0, cqeLine);
      }
    }

    plotData = plotData.map(x => {
      return { ...x, ...{ mode: 'lines', hoverlabel: { namelength: -1 } } };
    });

    const initialConditionIndex = plotData.findIndex(x => x.name == 'Initial Condition');

    for (let i = 0; i < plotData.length; i++) {
      if (i < initialConditionIndex) {
        plotData[i].legendgroup = "ellipse";
      } else {
        plotData[i].legendgroup = "loads";

        if (plotData[i].x.length <= 4 || plotData[i].y.length <= 4) {
          plotData[i].mode = 'lines+markers';
          plotData[i].marker = { size: 4 };
        }
      }
    }

    let count = 0;
    for (const trace of plotData) {
      if (trace.legendgroup === "loads") {
        const lineTypes = ['dot', 'dash', 'longdash', 'dashdot', 'longdashdot'];

        const dashType = count > 8
          ? lineTypes[(count - 9) % lineTypes.length]
          : 'solid';

        trace.line = { ...trace.line, dash: dashType };
        count++;
      }
    }

    this.plot.data = plotData;
    // setTimeout(() => { this.updateImageButtonOptions(); });
    this.isLoading.set(false);
  }

  get isDialogVisible(): boolean {
    return (this.needToCalculate || this.configMissing) && !this.dlpData?.errors
  }

  ngOnDestroy() {
    this.signalRfunc = null
    this.designLimitsPlotStore.reset();
  }
}
